import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  TextField,
  Button,
  
  Box,
  Typography,
  Avatar,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import {
  VpnKey as VpnKeyIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { SwalConfig } from '../../../utils/swalConfig';

const EditUserRole = () => {
  const { id } = useParams();
  const [formData, setFormData] = useState({
    id_user: '',
    id_bu: '',
    id_client: '',
    role: ''
  });
  
  const roleOptions = [
    'admin',
    'superadmin', 
    'user',
    'manager',
    'supervisor',
    'operator',
    'viewer',
    'editor',
    'moderator',
    'guest'
  ];
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const navigate = useNavigate();

  // Fetch Data
  const fetchData = async () => {
    try {
      setLoadingData(true);
      
      // Note: Business Unit is preserved from the API response but is intentionally not editable in the UI

      // Fetch Users
      const usersResponse = await fetch('https://api-sso-v2.eurekagroup.id/api/users', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (usersResponse.ok) {
        const usersResult = await usersResponse.json();
        setUsers(usersResult.data || usersResult);
      }

      // Fetch Clients
      const clientsResponse = await fetch('https://api-sso-v2.eurekagroup.id/api/clients', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (clientsResponse.ok) {
        const clientsResult = await clientsResponse.json();
        setClients(clientsResult.data || clientsResult);
      }

      // Fetch User Role Data
      const userRoleResponse = await fetch(`https://api-sso-v2.eurekagroup.id/api/user-roles/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (userRoleResponse.ok) {
        const userRoleResult = await userRoleResponse.json();
        const userRoleData = userRoleResult.data || userRoleResult;
        setFormData({
          // id_user and id_client in the response are objects; store their _id values so Autocomplete can match
          id_user: (userRoleData.id_user && userRoleData.id_user._id) ? userRoleData.id_user._id : (userRoleData.id_user || ''),
          // keep id_bu as string so we can preserve the original BU on submit, but we will remove the select from UI
          id_bu: (userRoleData.id_bu !== undefined && userRoleData.id_bu !== null) ? String(userRoleData.id_bu) : '',
          id_client: (userRoleData.id_client && userRoleData.id_client._id) ? userRoleData.id_client._id : (userRoleData.id_client || ''),
          role: userRoleData.role || ''
        });
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      Swal.fire({
        ...SwalConfig.error,
        title: 'Error!',
        text: 'Gagal memuat data'
      });
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Validation (BU is preserved and not editable on this form)
    if (!formData.id_user || !formData.id_client || !formData.role) {
      Swal.fire({
        ...SwalConfig.error,
        title: 'Error!',
        text: 'Semua field harus diisi'
      });
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`https://api-sso-v2.eurekagroup.id/api/user-roles/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          id_user: formData.id_user,
          id_bu: parseInt(formData.id_bu),
          id_client: formData.id_client,
          role: formData.role
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('User role updated:', result);
      
      SwalConfig.success(
        'Berhasil!',
        'User role berhasil diperbarui',
        2000
      );
      
      navigate('/sso/user-role');
    } catch (error) {
      console.error('Error updating user role:', error);
      Swal.fire({
        ...SwalConfig.error,
        title: 'Error!',
        text: `Gagal memperbarui user role: ${error.message}`
      });
    } finally {
      setLoading(false);
    }
  };


  if (loadingData) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: 400,
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ color: 'text.secondary' }}>
          Memuat data...
        </Typography>
      </Box>
    );
  }

  return (
    <div className="animate-fade-in">
      <Card sx={{ boxShadow: '0 8px 32px rgba(0,0,0,0.1)', borderRadius: 3 }}>
        <CardHeader 
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }} className="shadow-lg">
                <VpnKeyIcon />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }} className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Edit User Role
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }} className="text-gray-600">
                  Perbarui role user untuk client SSO
                </Typography>
              </Box>
            </Box>
          }
          action={
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/sso/user-role')}
              sx={{
                borderRadius: 2,
                px: 2,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Kembali
            </Button>
          }
          sx={{ pb: 2 }}
        />
        <CardContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={users}
                  getOptionLabel={(option) => `${option.nama_lengkap} (${option.email})`}
                  value={users.find(user => user._id === formData.id_user) || null}
                  onChange={(event, newValue) => {
                    setFormData(prev => ({
                      ...prev,
                      id_user: newValue ? newValue._id : ''
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="User"
                      variant="outlined"
                      required
                      placeholder="Cari user..."
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2
                        }
                      }}
                    />
                  )}
                  sx={{
                    '& .MuiAutocomplete-root': {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={clients}
                  getOptionLabel={(option) => option.client}
                  value={clients.find(client => client._id === formData.id_client) || null}
                  onChange={(event, newValue) => {
                    setFormData(prev => ({
                      ...prev,
                      id_client: newValue ? newValue._id : ''
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Client"
                      variant="outlined"
                      required
                      placeholder="Cari client..."
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2
                        }
                      }}
                    />
                  )}
                  sx={{
                    '& .MuiAutocomplete-root': {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }
                  }}
                />
              </Grid>
              
              {/* Business Unit is intentionally hidden from editing per requirements; value is preserved in state */}
              
              <Grid item xs={12} md={6}>
                <Autocomplete
                  freeSolo
                  options={roleOptions}
                  value={formData.role}
                  onChange={(event, newValue) => {
                    setFormData(prev => ({
                      ...prev,
                      role: newValue || ''
                    }));
                  }}
                  onInputChange={(event, newInputValue) => {
                    setFormData(prev => ({
                      ...prev,
                      role: newInputValue
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Role"
                      variant="outlined"
                      required
                      placeholder="Cari atau ketik role (contoh: admin, superadmin, user, dll)"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2
                        }
                      }}
                    />
                  )}
                  sx={{
                    '& .MuiAutocomplete-root': {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/sso/user-role')}
                    disabled={loading}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    Batal
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                    disabled={loading}
                    className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    {loading ? 'Memperbarui...' : 'Perbarui User Role'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>
    </div>
  );
};

export default EditUserRole;